import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import './Dashboard.css';

import { 
  administracion as iconAdmin,
  catalogos as iconCatalogos,
  pacientes as iconPacientes,
  logoEce as logoECE,
  iconTotales,
  iconEstadisticas,
  iconCrear,
  iconSalir,
  medicos,
  usuarios,
  roles,
  iconReligiones,
  iconNacionalidades,
  iconIdentificaciones,
  iconParentescos,
  iconEstadoCivil,
  iconOcupaciones,
  iconTiposSangre,
  iconTiposSangreIsbt,
  // ⬇️ AGREGAR ESTOS:
  tratamientos,
  diagnosticos,
  cie10,
  estudiosLaboratorio,
  especialidadesMedicas,
  gpc,
  antecedentes,
  antecedentesNoPat,
  defaultIcon
} from '../utils/commonIcons.js';

const API_URL = import.meta.env?.VITE_API_URL || 'http://localhost:3001/api';

// Componente StatCard optimizado con React.memo
const StatCard = React.memo(({ title, value, icon, className = '' }) => (
  <div className={`stat-card ${className}`}>
    <div className="stat-icon">
      <img src={icon} alt={title} className="icon-large" />
    </div>
    <div className="stat-content">
      <div className="stat-number">{value}</div>
      <div className="stat-label">{title}</div>
    </div>
  </div>
));

// Componente ModuleCard optimizado con React.memo
const ModuleCard = (({ modulo, onClick }) => {
  const getModuleIcon = (module) => {
    const moduleKey = module['001'];

    // Mapeo directo de iconos importados
    const iconMap = {
      // Módulos principales
      'usuarios': usuarios,
      'roles': roles,
      'medicos': medicos,
      'pacientes': iconPacientes,
      'administracion': iconAdmin,
      'modulos': iconAdmin,

      // Catálogos básicos
      'religiones': iconReligiones,
      'nacionalidades': iconNacionalidades,
      'identificaciones': iconIdentificaciones,
      'parentescos': iconParentescos,
      'estadocivil': iconEstadoCivil,
      'ocupaciones': iconOcupaciones,
      'tipossangre': iconTiposSangre,
      'tipossangreisbt': iconTiposSangreIsbt,

      // Catálogos médicos
      'alergias': tratamientos, // Usar icono de tratamientos como genérico
      'tiposalergias': tratamientos,
      'tipostratamiento': tratamientos,
      'tratamientos': tratamientos,
      'diagnosticos': diagnosticos,
      'diagnosticoscie10': cie10,
      'cie10': cie10,
      'estudioslaboratorio': estudiosLaboratorio,
      'especialidadesmedicas': especialidadesMedicas,
      'especialidades': especialidadesMedicas,
      'gpc': gpc,
      'guiaspracticaclinica': gpc,
      'antecedentes': antecedentes,
      'antecedentesnopat': antecedentesNoPat,
      'antecedentesnopatologicos': antecedentesNoPat
    };

    // Retornar ícono mapeado o ícono por defecto
    return iconMap[moduleKey] || defaultIcon;
  };

  const iconSrc = getModuleIcon(modulo);
  const moduleName = modulo['100']?.a || 'Módulo';
  const moduleDesc = modulo['100']?.b || '';

  return (
    <div className="module-card" onClick={onClick}>
      <div className="module-icon">
        <img 
          src={iconSrc} 
          alt={moduleName}
          className="icon-large"
          onError={(e) => {
            // Fallback si no encuentra el icono
            e.target.src = iconAdmin;
          }}
        />
      </div>
      <div className="module-content">
        <h3 className="module-name">{moduleName}</h3>
        {moduleDesc && <p className="module-desc">{moduleDesc}</p>}
      </div>
    </div>
  );
});

const Dashboard = () => {
  const { user, logout, userName, userEmail } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // Estados del dashboard
  const [modulosPorCategoria, setModulosPorCategoria] = useState({});
  const [esAdmin, setEsAdmin] = useState(false);
  const [totalModulos, setTotalModulos] = useState(0);
  const [estadisticas, setEstadisticas] = useState({
    totalRegistros: 0,
    registrosActivos: 0,
    totalCatalogos: 0,
    totalPacientes: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Función para obtener icono de categoría
  const obtenerIconoCategoria = useCallback((categoria) => {
    const iconos = {
      'sistema': '⚙️',
      'catalogos': '📚',
      'pacientes': '🏥', 
      'clinicos': '🩺',
      'reportes': '📊',
      'otros': '📋'
    };
    return iconos[categoria] || '📋';
  }, []);

  // Función para obtener título de categoría
  const obtenerTituloCategoria = useCallback((categoria) => {
    const titulos = {
      'sistema': 'Configuración del Sistema',
      'catalogos': 'Catálogos del Sistema', 
      'pacientes': 'Gestión de Pacientes/Medicos/Expedientes',
      'clinicos': 'Catálogos Clínicos',
      'reportes': 'Reportes y Analíticas',
      'otros': 'Otros Módulos'
    };
    return titulos[categoria] || categoria;
  }, []);

  // Función para navegar a módulo
  const navegarA = useCallback((ruta) => {
    if (ruta) {
      navigate(ruta);
    }
  }, [navigate]);

  // Función para cargar estadísticas generales
    const cargarEstadisticasSeguras = useCallback(async () => {
      const estadisticasSeguras = {
        totalRegistros: totalModulos || 0,
        registrosActivos: totalModulos || 0,
        totalCatalogos: 8, // Valor conocido de catálogos
        totalPacientes: 0
      };

      // Si es admin, intentar cargar estadísticas completas
      if (user?.['100']?.b === 'admin@ehrmx.com') {
        try {
          const [usuariosResp, catalogosResp, pacientesResp] = await Promise.all([
            axios.get(`${API_URL}/usuarios`).catch(() => ({ data: { datos: { usuarios: [] } } })),
            axios.get(`${API_URL}/catalogos`).catch(() => ({ data: { datos: [] } })),
            axios.get(`${API_URL}/pacientes/estadisticas`).catch(() => ({ data: { datos: { total: 0 } } }))
          ]);

          const totalUsuarios = usuariosResp.data.datos?.usuarios?.length || usuariosResp.data.datos?.length || 0;
          const totalCatalogos = catalogosResp.data.datos?.length || 0;
          const totalPacientes = pacientesResp.data.datos?.total || 0;
          
          return {
            totalRegistros: totalUsuarios + totalCatalogos + totalPacientes,
            registrosActivos: totalUsuarios + totalPacientes,
            totalCatalogos,
            totalPacientes
          };
        } catch (error) {
          console.error('⚠️ Error al cargar estadísticas de admin:', error);
        }
      }
      
      return estadisticasSeguras;
    }, [user]); 


  // Función para cargar dashboard
  const cargarDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // **CAMBIO: Usar endpoint que resuelve módulos desde roles**
      const [modulosResponse, estadisticasResponse] = await Promise.all([
        axios.get(`${API_URL}/usuarios/mis-modulos`), // ← NUEVO ENDPOINT
        cargarEstadisticasSeguras()
      ]);

      const { modulos, esAdmin: admin, totalModulos: total } = modulosResponse.data.datos;

      // Agrupar módulos por categoría
      const modulosPorCat = modulos.reduce((acc, modulo) => {
        const categoria = modulo['300']?.a || 'otros';
        if (!acc[categoria]) {
          acc[categoria] = [];
        }
        acc[categoria].push(modulo);
        return acc;
      }, {});

      setModulosPorCategoria(modulosPorCat);
      setEsAdmin(admin);
      setTotalModulos(total);
      setEstadisticas(estadisticasResponse);

    } catch (error) {
      console.error('❌ [Dashboard] Error al cargar dashboard:', error);
      setError(error);
    } finally {
      setLoading(false);
    }
  }, [user?.email]);



  // Función para recargar dashboard
  const recargarDashboard = useCallback(() => {
    cargarDashboard();
  }, [cargarDashboard]);

  // Función para logout
  const handleLogout = useCallback(() => {
    if (window.confirm('¿Estás seguro de que deseas cerrar sesión?')) {
      logout();
    }
  }, [logout]);

  // Effect para cargar dashboard al montar
  useEffect(() => {
    if (user) {
      cargarDashboard();
    }
  }, [user, cargarDashboard]);

  // Memoización de categorías ordenadas
  const categoriasOrdenadas = useMemo(() => {
    const orden = ['sistema', 'pacientes', 'catalogos', 'clinicos', 'reportes', 'otros'];
    return Object.keys(modulosPorCategoria).sort((a, b) => {
      const indexA = orden.indexOf(a);
      const indexB = orden.indexOf(b);
      return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
    });
  }, [modulosPorCategoria]);

  // Renderizado del loading
  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-left">
          <img src={logoECE} alt="ECE" className="app-icon" />
          <div className="header-info">
            <h1>{import.meta.env?.VITE_APP_NAME || 'EHR Sistema de Módulos'}</h1>
            <p>Gestión de Expedientes Clínicos Electrónicos</p>
          </div>
        </div>
        <div className="header-right">
          <div className="user-info">
            <div className="user-avatar">
              {userName.charAt(0).toUpperCase()}
            </div>
            <div className="user-details">
              <span className="user-name">{userName}</span>
              <span className="user-email">{userEmail}</span>
              {esAdmin && <span className="admin-badge">Administrador</span>}
            </div>
          </div>
          <button onClick={handleLogout} className="logout-button">
            <img src={iconSalir} alt="Cerrar sesión" className="btn-icon" />
            Cerrar Sesión
          </button>
        </div>
      </header>


      {/* Módulos por Categorías */}
      <main className="modules-main">
        {error && (
          <div className="error-banner">
            <div className="error-content">
              <h3>⚠️ Error al cargar módulos</h3>
              <p>No se pudieron cargar los módulos desde la API.</p>
              <button onClick={recargarDashboard} className="btn-retry">
                <img src={iconCrear} alt="Reintentar" className="btn-icon" />
                Reintentar Carga
              </button>
            </div>
          </div>
        )}

        {categoriasOrdenadas.length === 0 ? (
          <div className="empty-modules">
            <div className="empty-icon">
              <img src={iconTotales} alt="Sin módulos" className="icon-xlarge" />
            </div>
            <h3>📋 No hay módulos disponibles</h3>
            <p>El sistema no pudo cargar los módulos desde la base de datos.</p>
            <div className="debug-info">
              <p><strong>Posibles causas:</strong></p>
              <ul>
                <li>El endpoint /api/modulos/dashboard no responde correctamente</li>
                <li>Error de autenticación JWT</li>
                <li>El usuario no tiene módulos asignados</li>
                <li>La base de datos no contiene módulos</li>
              </ul>
            </div>
            <button onClick={recargarDashboard} className="btn-primary">
              <img src={iconCrear} alt="Reintentar" className="btn-icon" />
              🔄 Reintentar Carga
            </button>
          </div>
        ) : (
          categoriasOrdenadas.map(categoria => (
            <section key={categoria} className="category-section">
              <div className="category-header">
                <h2 className="category-title">
                  <span className="category-icon">{obtenerIconoCategoria(categoria)}</span>
                  {obtenerTituloCategoria(categoria)}
                </h2>
                <span className="category-count">
                  {modulosPorCategoria[categoria].length} módulos
                </span>
              </div>
              
              <div className="modules-grid">
                {modulosPorCategoria[categoria].map(modulo => (
                  <ModuleCard 
                    key={modulo._id || modulo['001']} 
                    modulo={modulo}
                    onClick={() => navegarA(modulo['400']?.a)}
                  />
                ))}
              </div>
            </section>
          ))
        )}
      </main>

      {/* Footer */}
      <footer className="dashboard-footer">
        <div className="footer-content">
          <p>
            Sistema EHR v{import.meta.env?.VITE_APP_VERSION || '1.0.0'} | 
            Módulos disponibles: {totalModulos} | 
            Usuario: {esAdmin ? 'Administrador' : 'Usuario'}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Dashboard;